import {useCallback} from 'react';

import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {Tag} from 'shells/tags-editor/tags-editor';

import type {TagColor} from 'nutshell-graphql-types';
import type {
    ContactPageFragment,
    ContactPageFragment_tagMaps_edges_node_creator,
    ContactUpdateInputFieldInput as ContactUpdateInput,
    UpdateContact as UpdateContactMutation,
    UpdateContactVariables as UpdateContactVariables,
} from 'nutshell-graphql-types';

import CONTACT_PAGE_FRAGMENT from '../fragments/contact-page-fragment.graphql';
import UPDATE_CONTACT from '../mutations/update-contact.graphql';

export const useUpdateContact = (): {
    updateContact: (
        input: ContactUpdateInput,
        contactData?: ContactPageFragment | null | undefined,
        territoryName?: string | null,
        optimisticTags?: Tag[]
    ) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const [updateContact, {loading, error}] = useMutation<
        UpdateContactMutation,
        UpdateContactVariables
    >(UPDATE_CONTACT);

    const updateContactWithOptimisticResponse = useCallback(
        (
            input: ContactUpdateInput,
            contactData?: ContactPageFragment | null,
            territoryName?: string | null,
            optimisticTags?: Tag[]
        ) => {
            if (!contactData || !hasOptimisticFields(input)) {
                return updateContact({variables: {input: {contact: input}}});
            }

            const updatedContact = getUpdatedContactFromInput(
                input,
                contactData,
                territoryName,
                optimisticTags
            );

            return updateContact({
                variables: {input: {contact: input}},
                optimisticResponse: {
                    // @ts-expect-error
                    __typename: 'Mutation',
                    contactUpdate: {
                        __typename: 'ContactUpdatePayload',
                        contact: updatedContact,
                    },
                },
                update: (proxy, mutationResults) => {
                    const contact =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.contactUpdate &&
                        mutationResults.data.contactUpdate.contact;

                    if (!contact) {
                        return;
                    }

                    proxy.writeFragment({
                        id: input.id,
                        fragment: CONTACT_PAGE_FRAGMENT,
                        fragmentName: 'ContactPageFragment',
                        data: {
                            ...contact,
                        },
                    });
                },
            });
        },
        [updateContact]
    );

    return {
        updateContact: updateContactWithOptimisticResponse,
        isLoading: loading,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
    };
};

const getUpdatedContactFromInput = (
    input: ContactUpdateInput,
    contactData: ContactPageFragment,
    territoryName?: string | null,
    optimisticTags?: Tag[]
): ContactPageFragment => {
    const updatedContact: ContactPageFragment = {...contactData};

    if (typeof input.name === 'string') {
        updatedContact.name = input.name;
    }

    if ('ownerId' in input && input.ownerId) {
        updatedContact.owner = {
            id: input.ownerId,
            __typename: input.ownerId.includes('users') ? 'User' : 'Team',
        };
    }

    if ('isDeleted' in input) {
        updatedContact.isDeleted = Boolean(input.isDeleted);
    }

    if ('territoryId' in input && input.territoryId && territoryName) {
        updatedContact.territory = {
            id: input.territoryId,
            name: territoryName,
            __typename: 'Territory' as const,
        };
    }

    if ('description' in input && typeof input.description === 'string') {
        updatedContact.description = input.description;
    }

    if ('tags' in input && input.tags && optimisticTags) {
        updatedContact.tagMaps = {
            __typename: 'TagMapsConnection',
            edges: optimisticTags.map((tag) => ({
                __typename: 'TagMapsEdge',
                node: {
                    __typename: 'Tag',
                    id: tag.id,
                    name: tag.name,
                    count: tag.count ?? 0,
                    tagType: 'CONTACT',
                    color: tag.color as TagColor,
                    createdTime: tag.createdTime ?? null,
                    creator: getOptimisticTagCreator(tag, contactData),
                    lastUsedTime: null,
                },
            })),
        };
    }
    return updatedContact;
};

const hasOptimisticFields = (input: ContactUpdateInput) => {
    return (
        'name' in input ||
        'ownerId' in input ||
        'isDeleted' in input ||
        'territoryId' in input ||
        'description' in input ||
        'tags' in input
    );
};

const getOptimisticTagCreator = (
    tag: Tag,
    contactData: ContactPageFragment
): ContactPageFragment_tagMaps_edges_node_creator => {
    const existingCreator = contactData.tagMaps?.edges.find((edge) => edge.node?.id === tag.id)
        ?.node?.creator;

    if (existingCreator) {
        return existingCreator;
    }

    // If the creator is coming from the dropdown, we need to guarantee it's not null
    if (tag.creator?.__typename === 'Origin') {
        return {
            __typename: 'Origin',
            id: tag.creator.id,
            name: tag.creator.name,
        };
    }

    return {
        __typename: 'User',
        id: tag.creator?.id ?? '',
        name: tag.creator?.name ?? '',
    };
};
